<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'

interface SkidEventItem {
  timestamp: string | Date
  eventUnit: string
  eventType: string
  totalCementOne: number
  totalCementTwo: number
  totalAccelerator: number
  totalAdditiveOne: number
  totalAdditiveTwo: number
  totalAdditiveThree: number
  totalAdditiveFour: number
  totalAdditiveFive: number
  totalAdditiveSix: number
  powerConsKWh: number
}

const props = defineProps({
  events: {
    type: Array as PropType<SkidEventItem[]>,
    required: true
  }
})

const searchQuery = ref('')
const fmt = (val: number | undefined | null) => {
  if (val === undefined || val === null) return '0.0'
  return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function calculateIncrementStats(values: number[]) {
  const increments: number[] = []
  
  for (let i = 1; i < values.length; i++) {
    const diff = values[i]! - values[i - 1]!
    if (diff > 0) {
      increments.push(diff)
    }
  }
  
  if (increments.length === 0) return { avg: 0, max: 0 }
  
  const sum = increments.reduce((a, b) => a + b, 0)
  return {
    avg: sum / increments.length,
    max: Math.max(...increments)
  }
}
function getEventUnitColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pump 1')) return 'info';
  if (t.includes('pump 2')) return 'success';
  if (t.includes('pump 3')) return 'error';
  if (t.includes('pump 4')) return 'warning';
  return 'default';
}
function getEventTypeColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('emergency')) return 'error';
  if (t.includes('reached')) return 'success';
  if (t.includes('pump start') || t.includes('pump stop')) return 'secondary';
  if (t.includes('hole change')) return 'info';
  if (t.includes('low level')) return 'warning';
  return 'default';
}

const filteredEvents = computed(() => {
  if (!searchQuery.value) return props.events
  
  const q = searchQuery.value.toLowerCase()
  return props.events.filter(evt => 
    (evt.eventUnit || '').toLowerCase().includes(q) || 
    (evt.eventType || '').toLowerCase().includes(q)
  )
})
const cement1Stats = computed(() => calculateIncrementStats(filteredEvents.value.map(e => e.totalCementOne)))
const cement2Stats = computed(() => calculateIncrementStats(filteredEvents.value.map(e => e.totalCementTwo)))

const headers = [
  { title: 'Timestamp', key: 'timestamp', align: 'start', width: '180px' },
  { title: 'Unit', key: 'eventUnit', align: 'start' },
  { title: 'Event Type', key: 'eventType', align: 'start' },
  { title: 'Cement 1', key: 'totalCementOne', align: 'end' },
  { title: 'Cement 2', key: 'totalCementTwo', align: 'end' },
  { title: 'Total accelerator', key: 'totalAccelerator', align: 'end' },
  { title: 'Total additive 1', key: 'totalAdditiveOne', align: 'end' },
  { title: 'Total additive 2', key: 'totalAdditiveTwo', align: 'end' },
  { title: 'Total additive 3', key: 'totalAdditiveThree', align: 'end' },
  { title: 'Total additive 4', key: 'totalAdditiveFour', align: 'end' },
  { title: 'Total additive 5', key: 'totalAdditiveFive', align: 'end' },
  { title: 'Total additive 6', key: 'totalAdditiveSix', align: 'end' },
  { title: 'Power (kWh)', key: 'powerConsKWh', align: 'end' },
] as const;
</script>

<template>
  <v-container fluid>
    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Cement 1 Stats
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center mb-4 border-b pb-2">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Avg Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement1Stats.avg) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Max Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement1Stats.max) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Cement 2 Stats
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <div class="d-flex justify-space-between align-center mb-4 border-b pb-2">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Avg Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement2Stats.avg) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 text-medium-emphasis font-weight-bold text-uppercase">Max Increment</span>
              <span class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(cement2Stats.max) }} <span class="text-caption text-medium-emphasis">kg</span></span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- System Event Log -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">System Event Log ({{ filteredEvents.length }})</span>
        <v-text-field
          v-model="searchQuery"
          placeholder="Filter by Unit or Event..."
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          bg-color="white"
          style="min-width: 250px;"
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="filteredEvents"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.timestamp="{ item }">
          <span class="text-body-2 text-medium-emphasis" style="white-space: nowrap;">
            {{ new Date(item.timestamp).toLocaleString() }}
          </span>
        </template>

        <template v-slot:item.eventUnit="{ item }">
          <v-chip :color="getEventUnitColor(item.eventUnit)" size="small" variant="tonal" class="font-weight-bold">
            {{ item.eventUnit }}
          </v-chip>
        </template>

        <template v-slot:item.eventType="{ item }">
          <v-chip
            :color="getEventTypeColor(item.eventType)"
            size="small"
            variant="tonal"
            class="font-weight-bold text-uppercase"
            label
          >
            {{ item.eventType }}
          </v-chip>
        </template>

        <template v-slot:item.powerConsKWh="{ item }">
          <span class="font-weight-bold text-green-darken-1 font-monospace">{{ item.powerConsKWh }}</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>